/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Load More Products Custom Element
 * 
 * Replaces pagination with a "Load More" button that progressively loads products via AJAX.
 * Integrates with the existing ProductFacet filtering system.
 */
class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();
    this.currentPage = parseInt(this.dataset.currentPage) || 1;
    this.totalPages = parseInt(this.dataset.totalPages) || 1;
    this.sectionId = this.dataset.sectionId;
    this.isLoading = false;
    this.originalText = this.textContent;
    this.loadedProductIds = new Set(); // Track which products we've already shown
    this.actualNextPage = this.currentPage + 1; // Track the actual next page to load
  }

  connectedCallback() {
    this.addEventListener('click', this.loadMore.bind(this));
    
    // Listen for filter changes to reset the load more state
    document.addEventListener('facet:criteria-changed', this.resetState.bind(this));
    
    // [EC-TS] On page 1: filter and fill gaps. On page 2+: load all previous content
    console.log('[EC-TS] connectedCallback - currentPage:', this.currentPage, 'totalPages:', this.totalPages);
    if (this.currentPage === 1) {
      console.log('[EC-TS] Running filterInitialProducts for page 1');
      this.filterInitialProducts();
    } else {
      console.log('[EC-TS] Running handlePageRefresh for page', this.currentPage);
      this.handlePageRefresh();
    }
    
    // Hide button if already at the last page
    this.updateVisibility();
  }

  // [EC-TS] Filter products that were rendered server-side on initial page load
  async filterInitialProducts() {
    console.log('[EC-TS] filterInitialProducts called');
    
    const productContainer = document.querySelector('.product-list__inner');
    if (!productContainer) {
      console.log('[EC-TS] No product container found');
      return;
    }
    
    // Get active size filters from URL
    const url = new URL(window.location);
    const urlParams = new URLSearchParams(url.search);
    let hasSizeFilters = false;
    
    for (const key of urlParams.keys()) {
      if (key.startsWith('filter.v.option.')) {
        hasSizeFilters = true;
        console.log('[EC-TS] Found size filter:', key, '=', urlParams.get(key));
      }
    }
    
    // Only filter if there are size filters
    if (!hasSizeFilters) {
      console.log('[EC-TS] No size filters in URL, skipping filter');
      return;
    }
    
    const products = productContainer.querySelectorAll('product-item');
    const originalCount = products.length;
    console.log('[EC-TS] Found products:', originalCount);
    
    if (products.length === 0) return;
    
    const filteredProducts = this.filterProductsBySize(products);
    console.log('[EC-TS] Filtered products:', filteredProducts.length, 'out of', originalCount);
    
    // Remove products that don't match
    let removedCount = 0;
    products.forEach(product => {
      if (!filteredProducts.includes(product)) {
        const productTitle = product.querySelector('.product-item-meta__title')?.textContent || 'Unknown';
        console.log('[EC-TS] Removing product:', productTitle);
        product.remove();
        removedCount++;
      }
    });
    
    console.log('[EC-TS] Removed', removedCount, 'products');
    
    // Track the IDs of products we're keeping on page 1
    const remainingProducts = productContainer.querySelectorAll('product-item');
    const currentProductCount = remainingProducts.length;
    remainingProducts.forEach(product => {
      const productId = product.getAttribute('data-product-id');
      if (productId) this.loadedProductIds.add(productId);
    });
    
    // [EC-TS] On page 1, fill gaps if we have fewer than target products (whether filtered or server-side limited)
    const productsPerPage = 24;
    if (this.currentPage === 1 && currentProductCount < productsPerPage && this.totalPages > 1) {
      const gapCount = productsPerPage - currentProductCount;
      console.log('[EC-TS] Page has', currentProductCount, 'products. Attempting to fill', gapCount, 'gaps from next pages...');
      await this.fillGapsFromNextPages(productsPerPage, currentProductCount);
    }
  }

  // [EC-TS] Load products from subsequent pages to fill the gaps
  async fillGapsFromNextPages(targetCount, currentCount) {
    const productContainer = document.querySelector('.product-list__inner');
    if (!productContainer) return;
    
    let pageToLoad = 2; // Start from page 2
    let addedCount = 0;
    const maxPagesToLoad = 3; // Safety limit
    
    console.log('[EC-TS] Target:', targetCount, 'Current:', currentCount, 'Need:', targetCount - currentCount);
    
    while (currentCount < targetCount && pageToLoad <= this.totalPages && pageToLoad <= maxPagesToLoad + 1) {
      console.log('[EC-TS] Loading page', pageToLoad, 'to fill gaps...');
      
      try {
        const url = new URL(window.location);
        url.searchParams.set('page', pageToLoad);
        url.searchParams.set('section_id', this.sectionId);
        
        const response = await fetch(url.toString());
        if (!response.ok) {
          console.log('[EC-TS] Failed to load page', pageToLoad);
          break;
        }
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProducts = doc.querySelectorAll('.product-list__inner product-item');
        
        if (newProducts.length === 0) {
          console.log('[EC-TS] No products found on page', pageToLoad);
          break;
        }
        
        // Filter the new products
        const filteredNewProducts = this.filterProductsBySize(newProducts);
        console.log('[EC-TS] Page', pageToLoad, ':', filteredNewProducts.length, 'of', newProducts.length, 'products match filter');
        
        // Add filtered products until we reach target
        for (let i = 0; i < filteredNewProducts.length && currentCount < targetCount; i++) {
          const product = filteredNewProducts[i];
          const productId = product.getAttribute('data-product-id');
          
          // Skip if we've already shown this product
          if (productId && this.loadedProductIds.has(productId)) {
            console.log('[EC-TS] Skipping duplicate product ID:', productId);
            continue;
          }
          
          const clonedProduct = product.cloneNode(true);
          clonedProduct.style.opacity = '1';
          clonedProduct.removeAttribute('reveal');
          productContainer.appendChild(clonedProduct);
          
          if (productId) this.loadedProductIds.add(productId);
          currentCount++;
          addedCount++;
        }
        
        console.log('[EC-TS] Added', addedCount, 'products. Total now:', currentCount);
        
        if (currentCount >= targetCount) {
          console.log('[EC-TS] Target reached');
          // Update the actual next page since we consumed products from page 2, 3, etc.
          this.actualNextPage = pageToLoad + 1;
          console.log('[EC-TS] Next Load More will start from page', this.actualNextPage);
          
          // DON'T update currentPage - we're still showing "page 1" to the user
          // but we've pre-loaded some products from page 2
          break;
        }
        
        pageToLoad++;
        this.actualNextPage = pageToLoad;
        
      } catch (error) {
        console.error('[EC-TS] Error loading page', pageToLoad, ':', error);
        break;
      }
    }
    
    console.log('[EC-TS] Gap filling complete. Final count:', currentCount);
  }

  async handlePageRefresh() {
    // If we're on page 2 or higher after a refresh, we need to load all pages up to current
    if (this.currentPage > 1) {
      console.log('[EC-TS] Page refresh detected on page', this.currentPage);
      this.setLoadingState(true);
      
      try {
        const productContainer = document.querySelector('.product-list__inner');
        if (!productContainer) {
          console.log('[EC-TS] No product container found');
          return;
        }
        
        // First, filter the products that are already loaded on this page
        const currentProducts = productContainer.querySelectorAll('product-item');
        console.log('[EC-TS] Found', currentProducts.length, 'products already on page', this.currentPage);
        
        const filteredCurrentProducts = this.filterProductsBySize(currentProducts);
        console.log('[EC-TS] After filtering:', filteredCurrentProducts.length, 'products match');
        
        // Remove non-matching products and track matching ones
        currentProducts.forEach(product => {
          const productId = product.getAttribute('data-product-id');
          if (!filteredCurrentProducts.includes(product)) {
            product.remove();
          } else if (productId) {
            this.loadedProductIds.add(productId);
          }
        });
        
        const productsPerPage = 24;
        const targetProductCount = productsPerPage * this.currentPage;
        console.log('[EC-TS] Target:', targetProductCount, 'products. Currently have:', filteredCurrentProducts.length);
        
        let serverPageToLoad = 1;
        
        // Load previous pages until we reach the target count
        while (serverPageToLoad < this.currentPage) {
          console.log('[EC-TS] Loading previous page', serverPageToLoad);
          
          await this.loadPage(serverPageToLoad, false, true); // Prepend previous pages
          
          const totalProducts = productContainer.querySelectorAll('product-item').length;
          console.log('[EC-TS] Total products now:', totalProducts);
          
          if (totalProducts >= targetProductCount) {
            console.log('[EC-TS] Reached target product count');
            break;
          }
          
          serverPageToLoad++;
        }
        
        // Update actualNextPage - the next server page to load
        this.actualNextPage = this.currentPage + 1;
        
        const finalCount = productContainer.querySelectorAll('product-item').length;
        console.log('[EC-TS] Page refresh complete. Showing', finalCount, 'products. Next load from server page', this.actualNextPage);
        
      } catch (error) {
        console.error('Failed to load previous pages on refresh:', error);
      }
      
      this.setLoadingState(false);
    }
  }

  async loadMore(event) {
    event.preventDefault();
    
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }
    
    this.setLoadingState(true);
    
    try {
      // Use actualNextPage if it's been set by gap filling, otherwise use currentPage + 1
      const nextPage = this.actualNextPage || this.currentPage + 1;
      console.log('[EC-TS] Load More: Loading page', nextPage, '(currentPage:', this.currentPage, ')');
      
      await this.loadPage(nextPage, false);
      
      // Increment currentPage to reflect we've shown one more "page" worth of content
      this.currentPage = this.currentPage + 1;
      this.actualNextPage = nextPage + 1; // Update for next load
      
      console.log('[EC-TS] Updated currentPage to', this.currentPage, ', actualNextPage to', this.actualNextPage);
      
      this.updateVisibility();
      
      // Update URL to show the "virtual" page number the user is on
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('page', this.currentPage);
      history.replaceState({}, '', newUrl.toString());
      
    } catch (error) {
      console.error('Load More failed:', error);
      // Show error state briefly, then restore button
      this.textContent = 'Error loading products';
      setTimeout(() => {
        this.setLoadingState(false);
      }, 2000);
      return;
    }
    
    this.setLoadingState(false);
  }

  async loadPage(pageNumber, replaceContent = false, prepend = false) {
    const url = new URL(window.location);
    url.searchParams.set('page', pageNumber);
    url.searchParams.set('section_id', this.sectionId);
    
    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Extract new products from the response
    const newProducts = doc.querySelectorAll('.product-list__inner .product-item');
    const productContainer = document.querySelector('.product-list__inner');
    
    if (newProducts.length > 0 && productContainer) {
      if (replaceContent) {
        // Clear existing products first
        productContainer.innerHTML = '';
      }
      
      // [EC-TS] Filter out products with sold-out size variants before appending
      const filteredProducts = this.filterProductsBySize(newProducts);
      
      // Extract and apply any styles from the entire response HTML
      const allStyleTags = doc.querySelectorAll('style');
      allStyleTags.forEach((styleTag, styleIndex) => {
        if (styleTag.textContent) {
          const styleId = `ajax-style-${Date.now()}-${styleIndex}`;
          if (!document.head.querySelector(`style[data-ajax-style="${styleId}"]`)) {
            const newStyleTag = document.createElement('style');
            newStyleTag.textContent = styleTag.textContent;
            newStyleTag.setAttribute('data-ajax-style', styleId);
            document.head.appendChild(newStyleTag);
          }
        }
      });
      
      // Append or prepend new products with proper opacity handling
      filteredProducts.forEach((product, index) => {
        const productId = product.getAttribute('data-product-id');
        
        // Skip duplicates
        if (productId && this.loadedProductIds.has(productId)) {
          console.log('[EC-TS] Skipping duplicate product on load:', productId);
          return;
        }
        
        const clonedProduct = product.cloneNode(true);
        
        // Ensure the product is fully visible
        clonedProduct.style.opacity = '1';
        clonedProduct.removeAttribute('reveal');
        
        // Remove any stagger animation attributes that might interfere
        if (clonedProduct.hasAttribute('style')) {
          const style = clonedProduct.getAttribute('style');
          if (style.includes('opacity:')) {
            clonedProduct.setAttribute('style', style.replace(/opacity:\s*[^;]+;?/g, 'opacity: 1;'));
          }
        }
        
        if (productContainer.hasAttribute('stagger-apparition')) {
          // Add a small delay for stagger effect but ensure opacity is 1
          setTimeout(() => {
            if (prepend) {
              productContainer.insertBefore(clonedProduct, productContainer.firstChild);
            } else {
              productContainer.appendChild(clonedProduct);
            }
            if (productId) this.loadedProductIds.add(productId);
            // Force opacity to 1 after appending and trigger any CSS recalculation
            requestAnimationFrame(() => {
              clonedProduct.style.opacity = '1';
              // Force a reflow to ensure styles are applied
              clonedProduct.offsetHeight;
            });
          }, index * 100);
        } else {
          if (prepend) {
            productContainer.insertBefore(clonedProduct, productContainer.firstChild);
          } else {
            productContainer.appendChild(clonedProduct);
          }
          if (productId) this.loadedProductIds.add(productId);
          // Ensure opacity is set immediately and trigger reflow
          clonedProduct.style.opacity = '1';
          clonedProduct.offsetHeight; // Force reflow
        }
      });
    }
  }

  // [EC-TS] Filter products based on active size filters
  filterProductsBySize(products) {
    console.log('[EC-TS] filterProductsBySize called with', products.length, 'products');
    
    // Get active size filters from URL
    const url = new URL(window.location);
    const urlParams = new URLSearchParams(url.search);
    const sizeFilters = {};
    
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filter.v.option.')) {
        const optionName = key.replace('filter.v.option.', '').toLowerCase();
        if (!sizeFilters[optionName]) {
          sizeFilters[optionName] = [];
        }
        sizeFilters[optionName].push(value.toLowerCase());
      }
    }
    
    console.log('[EC-TS] Size filters:', sizeFilters);
    
    // If no size filters, return all products
    if (Object.keys(sizeFilters).length === 0) {
      console.log('[EC-TS] No size filters, returning all products');
      return Array.from(products);
    }
    
    // Filter products based on available variants
    const filteredProducts = [];
    
    products.forEach(product => {
      const availableVariantsData = product.getAttribute('data-available-variants');
      
      // Skip products without variant data
      if (!availableVariantsData || availableVariantsData.trim() === '') {
        console.log('[EC-TS] Product has no variant data, skipping');
        return;
      }
      
      const availableSizes = availableVariantsData.split(',').map(size => size.trim().toLowerCase()).filter(s => s);
      console.log('[EC-TS] Product available sizes:', availableSizes);
      
      // Check if product has all the filtered sizes
      const isAvailable = Object.values(sizeFilters).every(filterValues => 
        filterValues.some(filterValue => availableSizes.includes(filterValue.toLowerCase().trim()))
      );
      
      console.log('[EC-TS] Product matches filter:', isAvailable);
      
      if (isAvailable) {
        filteredProducts.push(product);
      }
    });
    
    console.log('[EC-TS] Returning', filteredProducts.length, 'filtered products');
    return filteredProducts;
  }

  resetState() {
    // Reset to first page when filters change
    this.currentPage = 1;
    this.updateVisibility();
    
    // Always remove page parameter to keep URL clean
    const url = new URL(window.location);
    if (url.searchParams.has('page')) {
      url.searchParams.delete('page');
      history.replaceState({}, '', url.toString());
    }
  }

  setLoadingState(loading) {
    this.isLoading = loading;
    
    if (loading) {
      this.textContent = 'Loading...';
      this.setAttribute('disabled', '');
      this.style.opacity = '0.6';
    } else {
      this.textContent = this.originalText;
      this.removeAttribute('disabled');
      this.style.opacity = '1';
    }
  }

  updateVisibility() {
    // Hide button if we've shown all "virtual" pages OR if the next actual page to load exceeds total pages
    const nextActualPage = this.actualNextPage || this.currentPage + 1;
    
    if (this.currentPage >= this.totalPages || nextActualPage > this.totalPages) {
      console.log('[EC-TS] Hiding Load More - currentPage:', this.currentPage, 'nextActualPage:', nextActualPage, 'totalPages:', this.totalPages);
      this.style.display = 'none';
    } else {
      this.style.display = '';
    }
  }

  // Update total pages when new data is received (e.g., after filtering)
  updatePagination(currentPage, totalPages) {
    this.currentPage = currentPage;
    this.totalPages = totalPages;
    this.dataset.currentPage = currentPage;
    this.dataset.totalPages = totalPages;
    this.updateVisibility();
  }
}

// Register the custom element
customElements.define('load-more-products', LoadMoreProducts);

/**
 * Enhance ProductFacet to work with Load More functionality
 * This ensures the Load More button is properly updated when filters change
 */
document.addEventListener('DOMContentLoaded', function() {
  // Listen for the ProductFacet re-render events
  document.addEventListener('facet:criteria-changed', function(event) {
    // Wait for the ProductFacet to finish re-rendering
    setTimeout(() => {
      const loadMoreButton = document.querySelector('load-more-products');
      if (loadMoreButton) {
        // Extract pagination info from the newly rendered content
        const urlParams = new URLSearchParams(window.location.search);
        const currentPage = parseInt(urlParams.get('page')) || 1;
        
        // Look for pagination elements to determine total pages
        // This is a backup method in case the server doesn't provide the data
        const paginationNav = document.querySelector('.pagination__nav');
        let totalPages = 1;
        
        if (paginationNav) {
          const pageLinks = paginationNav.querySelectorAll('a[data-page], span[aria-current="page"]');
          pageLinks.forEach(link => {
            const pageNum = parseInt(link.textContent) || parseInt(link.getAttribute('data-page'));
            if (pageNum && pageNum > totalPages) {
              totalPages = pageNum;
            }
          });
        }
        
        // Update the Load More button with new pagination data
        loadMoreButton.updatePagination(currentPage, totalPages);
      }
    }, 100);
  });
});
